/**
 * useSubscription Hook
 *
 * Provides subscription state for route protection
 * Issue #703: 未決済ユーザーのダッシュボードアクセス制御
 * Issue #1788: 新規ユーザー登録後の認証ループ修正（リトライロジック追加）
 *
 * @created 2025-11-30
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { useAuthContext } from './useAuth';
import subscriptionService, {
  Subscription,
  SubscriptionError,
} from '../services/subscriptionService';
import logger from '../utils/logger';
import { useAuthStore, type PlanType } from '../store/useAuthStore';
import { SubscriptionStatusEnum } from '../types/enums';
import { scrubPiiFields } from '../utils/pii-scrubber';

/**
 * Issue #1788: 新規ユーザーセットアップ時のリトライ設定
 */
const NEW_USER_RETRY_CONFIG = {
  maxRetries: 3, // 最大リトライ回数
  retryDelayMs: 3000, // リトライ間隔（3秒）
};

const CLAIM_PLANS = ['free', 'light', 'standard', 'premium'] as const;
const CLAIM_PLAN_SET = new Set<PlanType>(CLAIM_PLANS);

function normalizeClaimPlan(raw: unknown): PlanType | null {
  if (typeof raw !== 'string') {
    return null;
  }

  return CLAIM_PLAN_SET.has(raw as PlanType) ? (raw as PlanType) : null;
}

function readPlanClaim(sessionClaims: unknown): PlanType | null {
  if (!sessionClaims || typeof sessionClaims !== 'object') {
    return null;
  }

  return normalizeClaimPlan((sessionClaims as { planId?: unknown }).planId);
}

function capturePlanClaimDrift(params: {
  claimPlan: PlanType | null;
  dbPlan: PlanType;
  userId: string;
  tenantId?: string;
  hasSubscription: boolean;
}) {
  const { claimPlan, dbPlan, userId, tenantId, hasSubscription } = params;

  if (!claimPlan || claimPlan === dbPlan) {
    return;
  }

  try {
    Sentry.captureMessage('subscription.claim_drift', {
      level: 'warning',
      tags: {
        service: 'useSubscription',
      },
      extra: scrubPiiFields({
        userId,
        tenantId,
        claimPlan,
        dbPlan,
        hasSubscription,
      }),
    });
  } catch (error) {
    logger.warn('[useSubscription] subscription.claim_drift capture failed', {
      error,
    });
  }
}

function isPreviewUser(user: unknown): user is { id: string; tenantId?: string } {
  return Boolean(
    user &&
      typeof user === 'object' &&
      (user as { isPreview?: unknown }).isPreview === true &&
      typeof (user as { id?: unknown }).id === 'string'
  );
}

function createPreviewSubscription(params: {
  userId: string;
  tenantId?: string;
}): Subscription {
  const now = new Date().toISOString();
  return {
    id: `preview-subscription-${params.userId}`,
    userId: params.userId,
    stripeCustomerId: 'preview-customer',
    stripeSubscriptionId: 'preview-subscription',
    status: SubscriptionStatusEnum.ACTIVE,
    planId: 'standard',
    currentPeriodStart: now,
    currentPeriodEnd: now,
    cancelAtPeriodEnd: false,
    tenantId: params.tenantId ?? 'preview-tenant',
    metadata: { source: 'preview-login' },
    createdAt: now,
    updatedAt: now,
  };
}

export interface UseSubscriptionReturn {
  /** Current subscription or null if none */
  subscription: Subscription | null;
  /** True if user has active subscription (status === 'active' only) */
  hasActiveSubscription: boolean;
  /** True while fetching subscription */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Refetch subscription data */
  refetch: () => Promise<void>;
}

/**
 * Hook to manage subscription state for route protection
 *
 * Design decisions (Issue #703):
 * - Only 'active' status is allowed (past_due, canceled, unpaid are blocked)
 * - Returns isLoading=true until subscription check completes (Issue #633 pattern)
 * - Automatically refetches when user changes
 *
 * Issue #1788: 新規ユーザー認証ループ修正
 * - user_not_found エラー時は自動リトライ（webhook処理待ち）
 * - 最大3回、3秒間隔でリトライ
 * - リトライ中はisLoading=trueを維持
 */
export function useSubscription(): UseSubscriptionReturn {
  const { user, isAuthenticated, isInitializing } = useAuthContext();
  const { sessionClaims } = useClerkAuth();
  const planClaim = readPlanClaim(sessionClaims);
  const isPreviewUserAccount = isPreviewUser(user);
  const userId = user?.id;
  const tenantId = (user as { tenantId?: string } | null | undefined)?.tenantId;
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Issue #1788: リトライカウンターをrefで管理（再レンダリングでリセットされない）
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchSubscription = useCallback(
    async (expectedUserId?: string) => {
      // Don't fetch if auth is still initializing or user is not authenticated
      if (isInitializing || !isAuthenticated || !userId) {
        setIsLoading(false);
        return;
      }

      // Issue #1788: ユーザーID不一致チェック（リトライ時のみ）
      // リトライ中にユーザーが変わった場合、古いリトライを中断
      if (expectedUserId && expectedUserId !== userId) {
        logger.info('[useSubscription] User changed during retry, aborting');
        retryCountRef.current = 0;
        return;
      }

      if (isPreviewUserAccount) {
        const previewSubscription = createPreviewSubscription({
          userId,
          tenantId,
        });
        setSubscription(previewSubscription);
        useAuthStore.getState().setPlan('standard');
        setError(null);
        setIsLoading(false);
        retryCountRef.current = 0;
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        logger.debug(
          '[useSubscription] Fetching subscription for user:',
          userId
        );
        const sub = await subscriptionService.getActiveSubscription(userId);
        setSubscription(sub);

        // Issue #1788: 成功時はリトライカウンターをリセット
        retryCountRef.current = 0;

        // Sync plan to auth store (Issue #1497: Fix plan detection for unpaid users)
        const setPlan = useAuthStore.getState().setPlan;
        const dbPlan: PlanType = sub?.planId ?? 'free';
        capturePlanClaimDrift({
          claimPlan: planClaim,
          dbPlan,
          userId,
          tenantId,
          hasSubscription: !!sub,
        });
        if (sub) {
          setPlan(dbPlan);
          logger.info('[useSubscription] Subscription found:', {
            id: sub.id,
            status: sub.status,
            planId: sub.planId,
          });
        } else {
          setPlan(dbPlan);
          logger.info(
            '[useSubscription] No subscription found for user:',
            userId
          );
        }
      } catch (err) {
        // Issue #1788: user_not_found エラー時はリトライ
        if (err instanceof SubscriptionError && err.type === 'user_not_found') {
          if (retryCountRef.current < NEW_USER_RETRY_CONFIG.maxRetries) {
            retryCountRef.current += 1;
            logger.info(
              `[useSubscription] User not found (new user setup), retrying in ${NEW_USER_RETRY_CONFIG.retryDelayMs}ms... (attempt ${retryCountRef.current}/${NEW_USER_RETRY_CONFIG.maxRetries})`
            );

            // リトライを非同期でスケジュール（isLoadingはtrueのまま維持）
            // Issue #1788: リトライ時にユーザーIDを渡して、ユーザー変更を検出可能にする
            retryTimeoutRef.current = setTimeout(() => {
              fetchSubscription(userId);
            }, NEW_USER_RETRY_CONFIG.retryDelayMs);
            return; // isLoadingをtrueのまま維持
          } else {
            // リトライ上限到達
            logger.warn(
              '[useSubscription] Max retries reached for new user setup'
            );
            setError(
              'ユーザー設定に時間がかかっています。ページを再読み込みしてください。'
            );
            setSubscription(null);
            useAuthStore.getState().setPlan('free');
          }
        } else {
          // その他のエラー
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to fetch subscription';
          logger.error('[useSubscription] Error fetching subscription:', err);
          setError(errorMessage);
          setSubscription(null);
          // Fallback to free plan on error (Issue #1497)
          useAuthStore.getState().setPlan('free');
        }
      } finally {
        // Issue #1788: リトライ中はisLoadingをtrueのまま維持するため、
        // 上のreturn文で早期終了した場合はここに到達しない
        setIsLoading(false);
      }
    },
    [
      userId,
      tenantId,
      isAuthenticated,
      isInitializing,
      planClaim,
      isPreviewUserAccount,
    ]
  );

  // Fetch subscription when user changes
  useEffect(() => {
    // Issue #1788: ユーザー変更時にリトライカウンターをリセット
    retryCountRef.current = 0;
    fetchSubscription();

    // Issue #1788: クリーンアップ - ペンディング中のリトライをキャンセル
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [fetchSubscription]);

  // Calculate hasActiveSubscription
  // IMPORTANT: Only 'active' status is allowed (Issue #703 decision)
  // past_due, canceled, unpaid are all blocked
  // Issue #770: subscription can be undefined (not just null), use truthiness check
  // Issue #1780: Use SubscriptionStatusEnum for type safety
  const hasActiveSubscription =
    !!subscription && subscription.status === SubscriptionStatusEnum.ACTIVE;

  return {
    subscription,
    hasActiveSubscription,
    isLoading: isInitializing || isLoading,
    error,
    refetch: fetchSubscription,
  };
}

export default useSubscription;
